import {
  liveAnnouncements,
  prospectContactInput,
  prospectsToWork,
  type ProspectOutcome,
  type ProspectRecord,
  CLIENT_MUTABLE_FIELDS,
  DOSSIER_MUTABLE_FIELDS,
  MUTATION_ENTITY,
  clientBusinessErrors,
  APPOINTMENT_STATUSES,
  interventionAppointmentInput,
  changedField,
  withOfficialCommune,
  clientCreateInput,
  commentCreateInput,
  autoRuleFor,
  currentPrice,
  discountErrors,
  type DiscountKind,
  dossierCreateInput,
  dossierSubmitErrors,
  partnerScheduleErrors,
  missingRequiredItems,
  maxBytesFor,
  PHOTO_CATEGORY,
  PORTAL_DEFAULT_DAYS,
  interventionCreateInput,
  dossierPaymentSummary,
  SAV_USER_TRANSITIONS,
  savCreateInput,
  type SavAction,
  type SavRecord,
  dossierTotals,
  formatEuros,
  paymentCreateInput,
  paymentMachine,
  remainingToCollect,
  type PaymentRecord,
  type PaymentStatus,
  interventionMachine,
  type InterventionStatus,
  type InterventionRecord,
  portalSecret,
  portalToken,
  type PortalChannel,
  CONTRACT_DOSSIER_FIELDS,
  canonicalJson,
  contractContent,
  signInput,
  strokesSchema,
  utf8,
  type ContractContent,
  type CompanyLegal,
  presentedTexts,
  type LegalTextRecord,
  type Strokes,
  fileCreateInput,
  formatBytes,
  sha256Hex,
  type FileKind,
  dossierMachine,
  findTransition,
  mutationSchema,
  uuidv7,
  valuesEqual,
  type ClientCreateInput,
  type ClientFields,
  type ClientRecord,
  type DossierCreateInput,
  type DossierFields,
  type DossierStatus,
  type EntityRecordMap,
  type Mutation,
  type MutationResult,
  type PullResponse,
  type PushResponse,
  type SyncEntity,
} from '@clim/domain';
import { ApiError, type UploadState } from './api-client';
import { applyOptimistic, type LocalActor } from './optimistic';
import {
  applyChanges,
  emptyMeta,
  emptyRecords,
  type OutboxEntry,
  type RecordsState,
  type SyncIssue,
  type SyncMeta,
  type BlobStore,
  type SyncStorage,
} from './storage';

export interface SyncTransport {
  push(body: { deviceId: string; mutations: unknown[] }): Promise<PushResponse>;
  pull(cursor: string, limit?: number): Promise<PullResponse>;
  /** progressive upload of file contents (CDC §23) */
  uploadStatus?(fileId: string): Promise<UploadState>;
  uploadChunk?(fileId: string, offset: number, bytes: Uint8Array): Promise<UploadState>;
}

export interface NewFile {
  kind: FileKind;
  categoryId?: string | null;
  name?: string | null;
  originalFilename?: string | null;
  mime: string;
  data: Uint8Array;
}

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'auth_required' | 'error';

export class LocalValidationError extends Error {
  constructor(
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type NewMutation = DistributiveOmit<Mutation, 'mutationId' | 'clientTs'>;

export interface SyncEngineOptions {
  storage: SyncStorage;
  transport: SyncTransport;
  deviceId: string;
  actor: LocalActor;
  pullLimit?: number;
  pushBatch?: number;
  /** debounce before an automatic sync after a local change (ms); 0 disables auto sync */
  autoSyncDelayMs?: number;
  /** local contents of files waiting to be sent */
  blobs?: BlobStore;
  /** SHA-256 (hex) — WebCrypto by default; the mobile app provides expo-crypto */
  hash?: (data: Uint8Array) => Promise<string>;
  /** CSPRNG — WebCrypto by default; the mobile app provides expo-crypto */
  randomBytes?: (n: number) => Uint8Array;
}

/**
 * Offline-first engine shared by web (IndexedDB) and mobile (SQLite):
 *  - reads come from the local copy + pending mutations applied optimistically;
 *  - writes are validated locally with the shared domain rules, then queued (outbox);
 *  - `sync()` pushes the outbox in order (idempotent mutation ids) then pulls changes.
 * The server is the only authority: rejected mutations surface as issues and vanish.
 */
export class SyncEngine {
  private records: RecordsState = emptyRecords();
  private outbox: OutboxEntry[] = [];
  /** acknowledged by the server, kept in the overlay until the following pull lands */
  private inflight: OutboxEntry[] = [];
  private meta: SyncMeta = emptyMeta();
  private views = new Map<SyncEntity, Map<string, EntityRecordMap[SyncEntity]>>();
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private _status: SyncStatus = 'idle';
  private _lastError: string | null = null;
  private revision = 0;
  /** bumped by reset(): a sync started before a reset must not write afterwards */
  private epoch = 0;
  /** files whose content is still only on this device, with bytes already sent */
  private localFiles = new Map<string, { sent: number; total: number }>();

  constructor(private readonly opts: SyncEngineOptions) {}

  async init(): Promise<void> {
    const state = await this.opts.storage.load();
    if (state) {
      this.records = state.records;
      this.outbox = state.outbox.sort((a, b) => a.seq - b.seq);
      this.meta = state.meta;
    }
    for (const id of (await this.opts.blobs?.list()) ?? []) this.localFiles.set(id, { sent: 0, total: 0 });
    this.invalidate();
  }

  /* ------------------------------------------------------------ observation */

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get status() {
    return this._status;
  }
  get lastError() {
    return this._lastError;
  }
  get pendingCount() {
    return this.outbox.length;
  }
  get issues(): SyncIssue[] {
    return this.meta.issues;
  }
  get lastSyncAt() {
    return this.meta.lastSyncAt;
  }
  /** number of files whose content is still waiting to be sent from this device */
  get pendingUploads() {
    return this.localFiles.size;
  }
  /** content still on this device only (not yet received by the server) → progress */
  uploadProgress(fileId: string): { sent: number; total: number } | null {
    return this.localFiles.get(fileId) ?? null;
  }
  /** local bytes of a file not yet sent (to display it offline) */
  async localContent(fileId: string): Promise<Uint8Array | null> {
    return this.localFiles.has(fileId) ? ((await this.opts.blobs?.read(fileId)) ?? null) : null;
  }
  /** changes each time local data changes (handy as a React dependency) */
  get version() {
    return this.revision;
  }

  private invalidate() {
    this.views.clear();
    this.revision++;
    for (const l of this.listeners) l();
  }

  private setStatus(s: SyncStatus, error: string | null = null) {
    this._status = s;
    this._lastError = error;
    this.revision++;
    for (const l of this.listeners) l();
  }

  /* ------------------------------------------------------------ reads */

  private view<E extends SyncEntity>(entity: E): Map<string, EntityRecordMap[E]> {
    const cached = this.views.get(entity);
    if (cached) return cached as Map<string, EntityRecordMap[E]>;
    const map = new Map<string, EntityRecordMap[E]>(
      Object.entries(this.records[entity]) as [string, EntityRecordMap[E]][],
    );
    const lookup = {
      client: (id: string) => this.get('client', id),
      dossier: (id: string) => this.get('dossier', id),
      item: (id: string) => this.get('catalog_item', id),
      brandLabel: (id: string) => this.records.catalog_brand[id]?.label ?? null,
      method: (id: string) => this.records.payment_method[id],
      // technician names known locally (server copies of interventions / dossier assignments)
      userName: (id: string) =>
        Object.values(this.records.intervention).find((i) => i.technicianId === id)?.technicianName ??
        Object.values(this.records.dossier)
          .flatMap((d) => d.assignments)
          .find((a) => a.userId === id)?.userName ??
        null,
    };
    for (const e of [...this.inflight, ...this.outbox]) {
      if (MUTATION_ENTITY[e.mutation.type] !== entity) continue;
      const id = e.mutation.entityId;
      const next = applyOptimistic(entity, map.get(id), e.mutation, this.opts.actor, lookup);
      if (next) map.set(id, next);
      else map.delete(id);
    }
    this.views.set(entity, map as Map<string, EntityRecordMap[SyncEntity]>);
    return map;
  }

  get<E extends SyncEntity>(entity: E, id: string): EntityRecordMap[E] | undefined {
    return this.view(entity).get(id);
  }

  list<E extends SyncEntity>(entity: E): EntityRecordMap[E][] {
    return [...this.view(entity).values()];
  }

  /** « À synchroniser » : état de synchronisation d'un enregistrement pour l'affichage. */
  syncState(entity: SyncEntity, id: string): 'synced' | 'pending' | 'syncing' {
    const entries = this.outbox.filter(
      (e) => e.mutation.entityId === id && MUTATION_ENTITY[e.mutation.type] === entity,
    );
    if (entries.some((e) => e.state === 'syncing')) return 'syncing';
    if (entries.length || this.inflight.some((e) => e.mutation.entityId === id)) return 'pending';
    return 'synced';
  }

  /** true while a local change on this record has not been confirmed by the server */
  isPending(entity: SyncEntity, id: string): boolean {
    return [...this.inflight, ...this.outbox].some(
      (e) => e.mutation.entityId === id && MUTATION_ENTITY[e.mutation.type] === entity,
    );
  }

  /* ------------------------------------------------------------ writes */

  async enqueue(m: NewMutation): Promise<string> {
    const mutation = { ...m, mutationId: uuidv7(), clientTs: new Date().toISOString() } as Mutation;
    const parsed = mutationSchema.safeParse(mutation);
    if (!parsed.success) {
      const fields = Object.fromEntries(
        parsed.error.issues.map((i) => [i.path.slice(1).join('.') || 'form', i.message]),
      );
      throw new LocalValidationError('Données invalides', fields);
    }
    const entry: OutboxEntry = {
      seq: this.meta.nextSeq,
      mutation,
      createdAt: new Date().toISOString(),
      attempts: 0,
      state: 'pending',
    };
    this.meta = { ...this.meta, nextSeq: this.meta.nextSeq + 1 };
    await this.opts.storage.addOutbox(entry, this.meta);
    this.outbox.push(entry);
    this.invalidate();
    this.scheduleSync();
    return mutation.mutationId;
  }

  async createClient(input: Omit<ClientCreateInput, 'id'> & { id?: string }): Promise<string> {
    const id = input.id ?? uuidv7();
    // commune des Antilles / Guyane : orthographe officielle, code postal cohérent (vérifié aussi par le serveur)
    const official = withOfficialCommune(input, 'postalCode', 'city');
    const parsed = clientCreateInput.safeParse({ ...official.value, id });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    const errors = { ...clientBusinessErrors(parsed.data), ...official.errors };
    if (Object.keys(errors).length) throw new LocalValidationError('Données invalides', errors);
    const { id: _id, ...payload } = official.value;
    await this.enqueue({ type: 'client.create', entityId: id, payload: payload as never });
    return id;
  }

  async updateClient(id: string, patch: Partial<ClientFields>): Promise<void> {
    const current = this.get('client', id);
    if (!current) throw new LocalValidationError('Client introuvable');
    let communeErrors: Record<string, string> = {};
    if ('postalCode' in patch || 'city' in patch) {
      const official = withOfficialCommune({ postalCode: current.postalCode, city: current.city, ...patch }, 'postalCode', 'city', changedField(patch, 'postalCode', 'city'));
      patch = { ...patch, postalCode: official.value.postalCode, city: official.value.city };
      communeErrors = official.errors;
    }
    const changes = diffChanges(current, patch, CLIENT_MUTABLE_FIELDS);
    if (!Object.keys(changes).length) return;
    const merged = { ...current, ...Object.fromEntries(Object.entries(changes).map(([k, v]) => [k, v.to])) };
    const errors = { ...clientBusinessErrors(merged as Partial<ClientFields>), ...communeErrors };
    if (Object.keys(errors).length) throw new LocalValidationError('Données invalides', errors);
    await this.enqueue({ type: 'client.update', entityId: id, baseVersion: current.version, changes });
  }

  async deleteClient(id: string): Promise<void> {
    const current = this.get('client', id);
    if (!current) return;
    if (this.list('dossier').some((d) => d.clientId === id)) {
      throw new LocalValidationError('Ce client a des dossiers : archivez-les d’abord');
    }
    await this.enqueue({ type: 'client.delete', entityId: id, baseVersion: current.version });
  }

  async createDossier(input: Omit<DossierCreateInput, 'id'> & { id?: string }): Promise<string> {
    const id = input.id ?? uuidv7();
    const official = withOfficialCommune(input, 'sitePostalCode', 'siteCity');
    if (Object.keys(official.errors).length) throw new LocalValidationError('Commune à choisir dans la liste', official.errors);
    input = official.value;
    const parsed = dossierCreateInput.safeParse({ ...input, id });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    if (!this.get('client', input.clientId))
      throw new LocalValidationError('Client introuvable', { clientId: 'Client introuvable' });
    const { id: _id, ...payload } = input;
    await this.enqueue({ type: 'dossier.create', entityId: id, payload: payload as never });
    await this.syncAutoLines(id);
    return id;
  }

  /**
   * Articles ajoutés d'office selon les conditions d'intervention (nacelle jusqu'à / au-dessus de 8 m) : ajoutés,
   * remplacés ou retirés quand le dossier change. Mutations ordinaires (hors ligne compris) : l'appareil et le
   * serveur ont toujours les mêmes lignes, donc le même contenu signé.
   */
  private async syncAutoLines(dossierId: string): Promise<void> {
    const d = this.get('dossier', dossierId);
    // chantier partenaire : pas de vente, donc pas d'article ajouté d'office
    if (!d || d.signature || d.partnerId || !['draft', 'to_correct'].includes(d.status)) return;
    const want = autoRuleFor(d);
    const items = this.list('catalog_item');
    const ruleOf = (itemId: string) => items.find((i) => i.id === itemId)?.autoRule ?? null;
    const autoLines = this.list('dossier_line').filter((l) => l.dossierId === dossierId && ruleOf(l.itemId));
    try {
      for (const l of autoLines) if (ruleOf(l.itemId) !== want) await this.removeLine(l.id);
      if (!want || autoLines.some((l) => ruleOf(l.itemId) === want)) return;
      const item = items
        .filter(
          (i) =>
            i.autoRule === want &&
            i.isActive &&
            (!i.dossierTypeIds.length || i.dossierTypeIds.includes(d.dossierTypeId)) &&
            (!i.territoryIds.length || i.territoryIds.includes(d.territoryId)) &&
            currentPrice(i.prices, d.territoryId),
        )
        .sort((a, b) => a.sortOrder - b.sortOrder)[0];
      if (item) await this.addLine(dossierId, item.id, 1);
    } catch {
      /* dossier non modifiable à ce stade : rien d'automatique */
    }
  }

  async updateDossier(id: string, patch: Partial<DossierFields>): Promise<void> {
    const current = this.get('dossier', id);
    if (!current) throw new LocalValidationError('Dossier introuvable');
    if ('sitePostalCode' in patch || 'siteCity' in patch) {
      const official = withOfficialCommune({ sitePostalCode: current.sitePostalCode, siteCity: current.siteCity, ...patch }, 'sitePostalCode', 'siteCity', changedField(patch, 'sitePostalCode', 'siteCity'));
      if (Object.keys(official.errors).length) throw new LocalValidationError('Commune à choisir dans la liste', official.errors);
      patch = { ...patch, sitePostalCode: official.value.sitePostalCode, siteCity: official.value.siteCity };
    }
    const changes = diffChanges(current, patch, DOSSIER_MUTABLE_FIELDS);
    if (!Object.keys(changes).length) return;
    if (changes.reserves) {
      for (const r of (changes.reserves.to ?? []) as { textId: string; comment: string | null }[]) {
        const t = this.get('legal_text', r.textId);
        if (t?.requiresComment && !r.comment?.trim())
          throw new LocalValidationError(`Réserve « ${t.title} » : commentaire obligatoire`, {
            reserves: 'Commentaire obligatoire',
          });
      }
    }
    const frozen = Object.keys(changes).filter((k) =>
      (CONTRACT_DOSSIER_FIELDS as readonly string[]).includes(k),
    );
    if (current.signature && frozen.length) {
      throw new LocalValidationError(
        'Contrat signé : ces informations sont figées (annulez la signature pour les modifier)',
        Object.fromEntries(frozen.map((k) => [k, 'Figé par la signature'])),
      );
    }
    await this.enqueue({ type: 'dossier.update', entityId: id, baseVersion: current.version, changes });
    if ('installAtHeight' in changes || 'installAccess' in changes || 'installHeightM' in changes)
      await this.syncAutoLines(id);
  }

  async transitionDossier(
    id: string,
    to: DossierStatus,
    opts: { reasonId?: string | null; comment?: string | null } = {},
  ): Promise<void> {
    const current = this.get('dossier', id);
    if (!current) throw new LocalValidationError('Dossier introuvable');
    const def = findTransition(dossierMachine, current.status, to, 'user');
    if (!def) throw new LocalValidationError('Changement de statut impossible');
    // chantier partenaire (sans vente) : brouillon → « À planifier » directement, jamais le parcours de vente
    if (current.status === 'draft' && to === 'to_schedule') {
      if (!current.partnerId)
        throw new LocalValidationError('Un dossier de vente passe par le contrôle avant la planification');
      const errors = partnerScheduleErrors(
        current,
        this.list('dossier_line').filter((l) => l.dossierId === id).length,
      );
      if (Object.keys(errors).length) throw new LocalValidationError(Object.values(errors).join(' ; '), errors);
    }
    if (current.partnerId && (to === 'submitted' || to === 'validated'))
      throw new LocalValidationError('Chantier partenaire : pas de vente, envoyez-le directement en planification');
    if (to === 'submitted') {
      const lineCount = this.list('dossier_line').filter((l) => l.dossierId === id).length;
      const photoCodes = this.list('dossier_file')
        .filter((f) => f.dossierId === id && f.kind === 'photo' && f.categoryId)
        .map((f) => this.get('reason', f.categoryId!)?.code)
        .filter((c): c is string => !!c);
      const labelOf = (code: string) =>
        this.list('reason').find((r) => r.category === PHOTO_CATEGORY && r.code === code)?.label ?? code;
      const errors = dossierSubmitErrors(
        current,
        this.get('client', current.clientId),
        this.get('dossier_type', current.dossierTypeId),
        lineCount,
        photoCodes,
        labelOf,
        !!current.signature,
      );
      const required = this.requirementsOf(id);
      if (required.length) errors.lines = required.join(' ; ');
      if (Object.keys(errors).length) throw new LocalValidationError('Dossier incomplet', errors);
    }
    if (def.reasonCategory) {
      const reason = opts.reasonId ? this.get('reason', opts.reasonId) : undefined;
      if (!reason || reason.category !== def.reasonCategory || !reason.isActive) {
        throw new LocalValidationError('Un motif est requis', { reasonId: 'Choisissez un motif' });
      }
      if (reason.requiresComment && !opts.comment?.trim()) {
        throw new LocalValidationError('Un commentaire est requis', { comment: 'Précisez le motif' });
      }
    }
    await this.enqueue({
      type: 'dossier.transition',
      entityId: id,
      payload: { from: current.status, to, reasonId: opts.reasonId ?? null, comment: opts.comment ?? null },
    });
  }

  async assignDossier(id: string, userId: string, role: 'commercial' | 'technician') {
    await this.enqueue({ type: 'dossier.assign', entityId: id, payload: { userId, role } });
  }

  async unassignDossier(id: string, userId: string, role: 'commercial' | 'technician') {
    await this.enqueue({ type: 'dossier.unassign', entityId: id, payload: { userId, role } });
  }

  async deleteDossier(id: string): Promise<void> {
    const current = this.get('dossier', id);
    if (!current) return;
    if (current.status !== 'draft') {
      throw new LocalValidationError('Seul un brouillon peut être supprimé ; sinon, annulez le dossier');
    }
    await this.enqueue({ type: 'dossier.delete', entityId: id, baseVersion: current.version });
  }

  async addComment(dossierId: string, visibility: 'public' | 'private', body: string): Promise<string> {
    const id = uuidv7();
    const parsed = commentCreateInput.safeParse({ id, dossierId, visibility, body });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    if (!this.get('dossier', dossierId)) throw new LocalValidationError('Dossier introuvable');
    await this.enqueue({ type: 'comment.create', entityId: id, payload: { dossierId, visibility, body } });
    return id;
  }

  /** Ajoute un produit / une prestation du catalogue (prix estimé localement, fixé par le serveur). */
  async addLine(
    dossierId: string,
    itemId: string,
    quantity: number,
    notes?: string | null,
    discount?: {
      discountHtCents: number;
      discountKind: DiscountKind | null;
      discountNote?: string | null;
      /** remise saisie en TTC exact */
      discountTtcCents?: number | null;
    },
    /** administrateur : prix propre à ce dossier (HT) et ancien prix barré (HT) */
    price?: {
      unitPriceHtCents?: number | null;
      compareAtPriceHtCents?: number | null;
      unitPriceTtcCents?: number | null;
      compareAtPriceTtcCents?: number | null;
    },
  ): Promise<string> {
    const dossier = this.get('dossier', dossierId);
    const item = this.get('catalog_item', itemId);
    if (!dossier) throw new LocalValidationError('Dossier introuvable');
    this.assertUnsigned(dossierId);
    // chantier partenaire : article pas encore ouvert à la vente accepté (il sert au barème de rémunération)
    if (!item || (!item.isActive && !dossier.partnerId))
      throw new LocalValidationError('Article indisponible', { itemId: 'Article indisponible' });
    if (!currentPrice(item.prices, dossier.territoryId))
      throw new LocalValidationError('Article sans tarif pour ce territoire', { itemId: 'Tarif manquant' });
    if (!(quantity > 0)) throw new LocalValidationError('Quantité invalide', { quantity: 'Quantité > 0' });
    const d = {
      discountHtCents: discount?.discountHtCents ?? 0,
      discountKind: discount?.discountKind ?? null,
    };
    const de = discountErrors({
      ...d,
      quantity,
      unitPriceHtCents:
        price?.unitPriceHtCents ?? currentPrice(item.prices, dossier.territoryId)!.priceHtCents,
    });
    if (Object.keys(de).length) throw new LocalValidationError(Object.values(de)[0]!, de);
    const id = uuidv7();
    await this.enqueue({
      type: 'line.create',
      entityId: id,
      payload: {
        dossierId,
        itemId,
        quantity,
        notes: notes ?? null,
        ...d,
        discountNote: discount?.discountNote ?? null,
        unitPriceHtCents: price?.unitPriceHtCents ?? null,
        compareAtPriceHtCents: price?.compareAtPriceHtCents ?? null,
        unitPriceTtcCents: price?.unitPriceTtcCents ?? null,
        compareAtPriceTtcCents: price?.compareAtPriceTtcCents ?? null,
        discountTtcCents: discount?.discountTtcCents ?? null,
      },
    });
    // option liée (ex. extension de garantie 5 ans → contrat d'entretien 5 ans) : ajoutée d'office si absente
    if (item.requiresItemIds?.length) {
      const present = new Set(this.list('dossier_line').filter((l) => l.dossierId === dossierId).map((l) => l.itemId));
      if (!item.requiresItemIds.some((rid) => present.has(rid))) {
        const req = item.requiresItemIds
          .map((rid) => this.get('catalog_item', rid))
          .find((r) => r && r.isActive && currentPrice(r.prices, dossier.territoryId));
        if (req) await this.addLine(dossierId, req.id, quantity).catch(() => undefined);
      }
    }
    return id;
  }

  async updateLine(
    id: string,
    patch: {
      quantity?: number;
      notes?: string | null;
      discountHtCents?: number;
      discountKind?: DiscountKind | null;
      discountNote?: string | null;
      unitPriceHtCents?: number;
      compareAtPriceHtCents?: number | null;
      unitPriceTtcCents?: number | null;
      compareAtPriceTtcCents?: number | null;
      discountTtcCents?: number | null;
    },
  ): Promise<void> {
    const current = this.get('dossier_line', id);
    if (!current) throw new LocalValidationError('Ligne introuvable');
    this.assertUnsigned(current.dossierId);
    if (patch.quantity !== undefined && !(patch.quantity > 0))
      throw new LocalValidationError('Quantité invalide', { quantity: 'Quantité > 0' });
    const next = { ...current, ...patch };
    const de = discountErrors({
      discountHtCents: next.discountHtCents ?? 0,
      discountKind: next.discountKind,
      quantity: next.quantity,
      unitPriceHtCents: next.unitPriceHtCents ?? 0,
    });
    if (current.unitPriceHtCents !== null && Object.keys(de).length)
      throw new LocalValidationError(Object.values(de)[0]!, de);
    const changes = diffChanges(current, patch, [
      'quantity',
      'notes',
      'discountHtCents',
      'discountKind',
      'discountNote',
      'unitPriceHtCents',
      'compareAtPriceHtCents',
      'unitPriceTtcCents',
      'compareAtPriceTtcCents',
      'discountTtcCents',
    ]);
    if (!Object.keys(changes).length) return;
    await this.enqueue({ type: 'line.update', entityId: id, baseVersion: current.version, changes });
  }

  async removeLine(id: string): Promise<void> {
    const line = this.get('dossier_line', id);
    if (!line) return;
    this.assertUnsigned(line.dossierId);
    await this.enqueue({ type: 'line.delete', entityId: id });
  }

  private assertUnsigned(dossierId: string) {
    if (this.get('dossier', dossierId)?.signature)
      throw new LocalValidationError(
        'Contrat signé : produits figés (annulez la signature pour les modifier)',
      );
  }

  /** Textes juridiques en vigueur sur l'appareil (conditions, consentements, modèles de réserves). */
  legalTexts(kind: LegalTextRecord['kind']): LegalTextRecord[] {
    return this.list('legal_text')
      .filter((t) => t.kind === kind)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  }

  /**
   * Textes à présenter pour CE dossier (pack juridique) : CGV actives, cases selon leurs
   * conditions (C5 exécution anticipée, C6 réserves…), mention RGPD, formulaire de rétractation.
   */
  signaturePlan(dossierId: string, company: CompanyLegal | null | undefined) {
    const d = this.get('dossier', dossierId);
    if (!d) throw new LocalValidationError('Dossier introuvable');
    const client = this.get('client', d.clientId);
    if (!client) throw new LocalValidationError('Client introuvable');
    return presentedTexts(this.list('legal_text'), { clientKind: client.kind, dossier: d, company });
  }

  /**
   * Récapitulatif signé (§4.12) : exactement le contenu dont l'empreinte sera vérifiée par le
   * serveur. `company` = coordonnées légales de l'entreprise (paramètres, disponibles hors ligne).
   */
  contractPreview(
    dossierId: string,
    opts: {
      company: CompanyLegal | null | undefined;
      signerName: string;
      signerQuality?: 'client' | 'representative';
      consentIds: string[];
      signedAt?: string;
    },
  ): ContractContent {
    const d = this.get('dossier', dossierId)!;
    const plan = this.signaturePlan(dossierId, opts.company);
    const missingTemplate = d.reserves.find((r) => !this.get('legal_text', r.textId));
    if (missingTemplate)
      throw new LocalValidationError(
        'Un modèle de réserve a été désactivé : retirez la réserve ou synchronisez',
      );
    return contractContent({
      company: opts.company,
      dossier: {
        ...d,
        typeLabel: this.get('dossier_type', d.dossierTypeId)?.label ?? '',
        installWallTypeLabel: d.installWallTypeId
          ? (this.get('reason', d.installWallTypeId)?.label ?? null)
          : null,
      },
      client: this.get('client', d.clientId)!,
      lines: this.list('dossier_line')
        .filter((l) => l.dossierId === dossierId)
        .map((l) => ({ ...l, unitPriceHtCents: l.unitPriceHtCents ?? 0 })),
      texts: {
        terms: plan.terms,
        consents: plan.consents,
        privacy: plan.privacy,
        withdrawal: plan.withdrawalForm,
        reserves: d.reserves.map((r) => this.get('legal_text', r.textId)!),
      },
      acceptedConsentIds: opts.consentIds,
      signer: { name: opts.signerName, quality: opts.signerQuality ?? 'client' },
      signedAt: opts.signedAt ?? new Date().toISOString(),
    });
  }

  /** Signature sur place (§4.13) — possible hors ligne ; le serveur vérifie l'empreinte. */
  /** Options liées non remplies (ex. extension de garantie 5 ans sans contrat d'entretien 5 ans). */
  requirementsOf(dossierId: string): string[] {
    const lines = this.list('dossier_line').filter((l) => l.dossierId === dossierId);
    return missingRequiredItems(lines, (id) => this.get('catalog_item', id));
  }

  async signDossier(
    dossierId: string,
    opts: {
      company: CompanyLegal | null | undefined;
      signerName: string;
      signerQuality?: 'client' | 'representative';
      consentIds: string[];
      strokes: Strokes;
    },
  ): Promise<string> {
    const d = this.get('dossier', dossierId);
    if (!d) throw new LocalValidationError('Dossier introuvable');
    if (d.signature) throw new LocalValidationError('Dossier déjà signé');
    if (!['draft', 'to_correct'].includes(d.status))
      throw new LocalValidationError('Ce dossier ne peut plus être signé à ce stade');
    const required = this.requirementsOf(dossierId);
    if (required.length) throw new LocalValidationError(required.join(' ; '), { lines: required.join(' ; ') });
    const plan = this.signaturePlan(dossierId, opts.company);
    const missing = plan.consents.filter((t) => t.required && !opts.consentIds.includes(t.id));
    if (missing.length)
      throw new LocalValidationError('Consentement obligatoire non accepté', {
        consentIds: missing.map((t) => t.title).join(', '),
      });
    const accepted = opts.consentIds.filter((id) => plan.consents.some((t) => t.id === id));
    const strokes = strokesSchema.safeParse(opts.strokes);
    if (!strokes.success)
      throw new LocalValidationError(strokes.error.issues[0]?.message ?? 'Signature invalide', {
        strokes: 'Signature invalide',
      });
    const signedAt = new Date().toISOString();
    const content = this.contractPreview(dossierId, { ...opts, consentIds: accepted, signedAt });
    const signatureId = uuidv7();
    const payload = {
      signatureId,
      signerName: opts.signerName.trim(),
      signerQuality: opts.signerQuality ?? 'client',
      termIds: plan.terms.map((t) => t.id),
      presentedConsentIds: plan.consents.map((t) => t.id),
      privacyId: plan.privacy?.id ?? null,
      withdrawalFormId: plan.withdrawalForm?.id ?? null,
      consentIds: accepted,
      strokes: strokes.data,
      contentHash: await (this.opts.hash ?? sha256Hex)(utf8(canonicalJson(content))),
      signedAt,
    };
    const parsed = signInput.safeParse(payload);
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    await this.enqueue({ type: 'dossier.sign', entityId: dossierId, payload });
    return signatureId;
  }

  async voidSignature(dossierId: string, reason: string): Promise<void> {
    const d = this.get('dossier', dossierId);
    if (!d?.signature) throw new LocalValidationError('Aucune signature à annuler');
    if (reason.trim().length < 3)
      throw new LocalValidationError('Motif obligatoire', { reason: 'Motif obligatoire' });
    await this.enqueue({
      type: 'dossier.void_signature',
      entityId: dossierId,
      payload: { reason: reason.trim() },
    });
  }

  /**
   * Adds a photo / document: content kept locally, metadata queued like any change; the
   * content is sent in chunks at the next syncs (resumable), then removed from the device.
   */
  async addFile(dossierId: string, file: NewFile): Promise<string> {
    const blobs = this.opts.blobs;
    if (!blobs) throw new LocalValidationError('Ajout de fichiers indisponible sur cet appareil');
    if (!this.get('dossier', dossierId)) throw new LocalValidationError('Dossier introuvable');
    const max = maxBytesFor(file.kind);
    if (file.data.byteLength > max) {
      throw new LocalValidationError(
        `Fichier trop volumineux (${formatBytes(file.data.byteLength)}, maximum ${formatBytes(max)})`,
      );
    }
    if (file.categoryId) {
      const cat = this.get('reason', file.categoryId);
      // vidéos et photos partagent les mêmes catégories (intérieur, extérieur, conditions de pose…)
      if (!cat || (file.kind !== 'document' && cat.category !== PHOTO_CATEGORY))
        throw new LocalValidationError('Catégorie invalide', { categoryId: 'Catégorie invalide' });
    }
    const id = uuidv7();
    const payload = {
      dossierId,
      kind: file.kind,
      categoryId: file.categoryId ?? null,
      name: file.name ?? '',
      originalFilename: file.originalFilename ?? null,
      mime: file.mime,
      sizeBytes: file.data.byteLength,
      sha256: await (this.opts.hash ?? sha256Hex)(file.data),
    };
    const parsed = fileCreateInput.safeParse({ ...payload, id });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    // content first: a crash between the two leaves an orphan blob, pruned at the next sync
    await blobs.put(id, file.data);
    this.localFiles.set(id, { sent: 0, total: file.data.byteLength });
    await this.enqueue({ type: 'file.create', entityId: id, payload });
    return id;
  }

  async updateFile(id: string, patch: { name?: string; categoryId?: string | null }): Promise<void> {
    const current = this.get('dossier_file', id);
    if (!current) throw new LocalValidationError('Fichier introuvable');
    if (patch.name !== undefined && current.kind === 'document' && !patch.name.trim())
      throw new LocalValidationError('Nom obligatoire', { name: 'Nom obligatoire' });
    const changes = diffChanges(current, patch, ['name', 'categoryId']);
    if (!Object.keys(changes).length) return;
    await this.enqueue({ type: 'file.update', entityId: id, baseVersion: current.version, changes });
  }

  async removeFile(id: string): Promise<void> {
    if (!this.get('dossier_file', id)) return;
    await this.enqueue({ type: 'file.delete', entityId: id });
  }

  /* ------------------------------------------------------------ planning (§8, §9) */

  async createIntervention(
    dossierId: string,
    opts: {
      scheduledDate?: string | null;
      slotStart?: string | null;
      slotEnd?: string | null;
      needsNacelle?: boolean;
      notes?: string | null;
      savId?: string | null;
    } = {},
  ): Promise<string> {
    const d = this.get('dossier', dossierId);
    if (!d) throw new LocalValidationError('Dossier introuvable');
    if (opts.savId) {
      const sav = this.get('sav', opts.savId);
      if (!sav || sav.dossierId !== dossierId) throw new LocalValidationError('SAV introuvable');
      if (sav.status === 'pending_validation')
        throw new LocalValidationError('Le SAV doit d’abord être validé');
      if (!['open', 'in_progress', 'done'].includes(sav.status))
        throw new LocalValidationError('SAV clôturé');
    } else if (!['to_schedule', 'in_progress'].includes(d.status))
      throw new LocalValidationError(
        'Le dossier doit être contrôlé et « À planifier » avant d’être planifié',
      );
    const id = uuidv7();
    const payload = {
      dossierId,
      scheduledDate: opts.scheduledDate ?? null,
      slotStart: opts.slotStart ?? null,
      slotEnd: opts.slotEnd ?? null,
      needsNacelle: opts.needsNacelle,
      notes: opts.notes ?? null,
      savId: opts.savId ?? null,
    };
    const parsed = interventionCreateInput.safeParse({ ...payload, id });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    await this.enqueue({ type: 'intervention.create', entityId: id, payload });
    return id;
  }

  async updateIntervention(
    id: string,
    patch: Partial<
      Pick<InterventionRecord, 'scheduledDate' | 'slotStart' | 'slotEnd' | 'needsNacelle' | 'notes'>
    >,
  ): Promise<void> {
    const current = this.get('intervention', id);
    if (!current) throw new LocalValidationError('Intervention introuvable');
    const changes = diffChanges(current, patch, [
      'scheduledDate',
      'slotStart',
      'slotEnd',
      'needsNacelle',
      'notes',
    ]);
    if (!Object.keys(changes).length) return;
    await this.enqueue({ type: 'intervention.update', entityId: id, baseVersion: current.version, changes });
  }

  /**
   * Rendez-vous fixé par le poseur après avoir contacté le client (fonctionne hors ligne) : date et heure, case
   * « client contacté » obligatoire ; l'intervention devient « Planifiée ».
   */
  async setInterventionAppointment(
    id: string,
    input: { scheduledDate: string; slotStart: string; slotEnd?: string | null; clientContacted: boolean; comment?: string | null },
  ): Promise<void> {
    const current = this.get('intervention', id);
    if (!current) throw new LocalValidationError('Intervention introuvable');
    if (current.technicianId !== this.opts.actor.userId)
      throw new LocalValidationError('Seul le technicien de l’intervention fixe le rendez-vous');
    if (!APPOINTMENT_STATUSES.includes(current.status))
      throw new LocalValidationError('Rendez-vous non modifiable pour cette intervention');
    const parsed = interventionAppointmentInput.safeParse(input);
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    await this.enqueue({ type: 'intervention.appointment', entityId: id, payload: parsed.data });
  }

  /** Transition d'intervention, validée localement par la machine partagée ; le serveur tranche. */
  async transitionIntervention(
    id: string,
    to: InterventionStatus,
    opts: {
      reasonId?: string | null;
      comment?: string | null;
      technicianId?: string | null;
      scheduledDate?: string | null;
      slotStart?: string | null;
      slotEnd?: string | null;
    } = {},
  ): Promise<void> {
    const current = this.get('intervention', id);
    if (!current) throw new LocalValidationError('Intervention introuvable');
    const defs = interventionMachine.transitions.filter(
      (t) => t.from.includes(current.status) && t.to === to,
    );
    if (!defs.length) throw new LocalValidationError('Changement de statut impossible');
    const def =
      defs.find((t) =>
        t.permission === 'intervention.execute' ? current.technicianId === this.opts.actor.userId : true,
      ) ?? defs[0]!;
    if (def.reasonCategory) {
      const reason = opts.reasonId ? this.get('reason', opts.reasonId) : undefined;
      if (!reason || reason.category !== def.reasonCategory || !reason.isActive)
        throw new LocalValidationError('Un motif est requis', { reasonId: 'Choisissez un motif' });
      if (reason.requiresComment && !opts.comment?.trim())
        throw new LocalValidationError('Un commentaire est requis', { comment: 'Précisez le motif' });
    }
    if (to === 'proposed' && !opts.technicianId)
      throw new LocalValidationError('Choisissez le technicien', { technicianId: 'Technicien obligatoire' });
    if (to === 'scheduled' && !(opts.scheduledDate ?? current.scheduledDate))
      throw new LocalValidationError('Date d’intervention obligatoire', {
        scheduledDate: 'Date obligatoire',
      });
    const payload: Record<string, unknown> = {
      from: current.status,
      to,
      reasonId: opts.reasonId ?? null,
      comment: opts.comment ?? null,
      technicianId: opts.technicianId ?? null,
    };
    for (const k of ['scheduledDate', 'slotStart', 'slotEnd'] as const)
      if (opts[k] !== undefined) payload[k] = opts[k];
    await this.enqueue({ type: 'intervention.transition', entityId: id, payload: payload as never });
  }

  /** « Prendre en charge ma tournée » : chacune de mes interventions planifiées du jour (hors ligne possible). */
  async takeTour(day: string): Promise<number> {
    const mine = this.list('intervention').filter(
      (i) => i.technicianId === this.opts.actor.userId && i.scheduledDate === day && i.status === 'scheduled',
    );
    for (const i of mine) await this.transitionIntervention(i.id, 'taken_charge');
    return mine.length;
  }

  /* ------------------------------------------------------------ SAV (§12) */

  savsOf(dossierId: string): SavRecord[] {
    return this.list('sav')
      .filter((s) => s.dossierId === dossierId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  /** SAV sur une installation terminée — possible hors ligne ; « À valider » si je ne peux pas valider. */
  async createSav(
    dossierId: string,
    input: { reasonId: string; description?: string | null },
  ): Promise<string> {
    const d = this.get('dossier', dossierId);
    if (!d) throw new LocalValidationError('Dossier introuvable');
    if (d.status !== 'completed')
      throw new LocalValidationError('Un SAV se crée sur une installation terminée');
    const reason = this.get('reason', input.reasonId);
    if (!reason || reason.category !== 'sav_reason' || !reason.isActive)
      throw new LocalValidationError('Choisissez un motif', { reasonId: 'Motif obligatoire' });
    if (reason.requiresComment && !input.description?.trim())
      throw new LocalValidationError('Précisez le motif', { description: 'Commentaire obligatoire' });
    const id = uuidv7();
    const payload = { dossierId, reasonId: reason.id, description: input.description?.trim() || null };
    const parsed = savCreateInput.safeParse({ ...payload, id });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    await this.enqueue({ type: 'sav.create', entityId: id, payload });
    return id;
  }

  async transitionSav(
    id: string,
    action: SavAction,
    opts: { comment?: string | null; causeId?: string | null } = {},
  ): Promise<void> {
    const s = this.get('sav', id);
    if (!s) throw new LocalValidationError('SAV introuvable');
    const def = SAV_USER_TRANSITIONS[action]!;
    if (!def.from.includes(s.status)) throw new LocalValidationError('Action impossible pour ce SAV');
    if (def.needs === 'comment' && !opts.comment?.trim())
      throw new LocalValidationError('Commentaire obligatoire', { comment: 'Commentaire obligatoire' });
    if (def.needs === 'cause') {
      const cause = opts.causeId ? this.get('reason', opts.causeId) : undefined;
      if (!cause || cause.category !== 'sav_cause')
        throw new LocalValidationError('Cause de clôture obligatoire', { causeId: 'Choisissez la cause' });
      if (cause.requiresComment && !opts.comment?.trim())
        throw new LocalValidationError('Commentaire obligatoire', { comment: 'Précisez' });
    }
    await this.enqueue({
      type: 'sav.transition',
      entityId: id,
      payload: {
        action: action as 'validate' | 'reject' | 'close' | 'cancel',
        comment: opts.comment?.trim() || null,
        causeId: opts.causeId ?? null,
      },
    });
  }

  /* ------------------------------------------------------------ paiements (§10) */

  /**
   * Total signé du dossier (plafond des encaissements) : celui confirmé par le serveur, sinon — pour
   * une signature faite hors ligne — le total des produits, figés par la signature.
   */
  signedTotal(dossierId: string): number | null {
    const d = this.get('dossier', dossierId);
    if (!d?.signature) return null;
    if (d.signature.totalTtcCents != null) return d.signature.totalTtcCents;
    return this.totals(dossierId).ttcCents;
  }

  /** Totaux du dossier (remises déduites) ; lignes aux prix masqués ignorées. */
  totals(dossierId: string) {
    return dossierTotals(
      this.list('dossier_line')
        .filter((l) => l.dossierId === dossierId && l.unitPriceHtCents !== null)
        .map((l) => ({
          ...l,
          unitPriceHtCents: l.unitPriceHtCents!,
          discountHtCents: l.discountHtCents ?? 0,
        })),
    );
  }

  paymentsOf(dossierId: string): PaymentRecord[] {
    return this.list('payment')
      .filter((p) => p.dossierId === dossierId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  /** À payer / Partiellement payé / Payé / En cours / Remboursé, payé, solde, reste encaissable. */
  paymentSummary(dossierId: string) {
    const total = this.signedTotal(dossierId);
    return total === null ? null : dossierPaymentSummary(total, this.paymentsOf(dossierId));
  }

  /** Encaissement (espèces, chèque… ou carte : le lien Stripe est créé à la synchronisation). */
  async createPayment(
    dossierId: string,
    input: {
      methodId: string;
      amountCents: number;
      isDeposit?: boolean;
      receivedOn?: string | null;
      reference?: string | null;
      comment?: string | null;
    },
  ): Promise<string> {
    const total = this.signedTotal(dossierId);
    if (total === null) throw new LocalValidationError('Le client doit d’abord signer le bon de commande');
    const method = this.records.payment_method[input.methodId];
    if (!method || !method.isActive)
      throw new LocalValidationError('Moyen de paiement indisponible', {
        methodId: 'Choisissez un moyen actif',
      });
    if (!method.configured)
      throw new LocalValidationError(`${method.label} : pas encore configuré par l’administrateur`, {
        methodId: 'Indisponible',
      });
    if (method.requiresReference && !input.reference?.trim())
      throw new LocalValidationError('Référence obligatoire', {
        reference: method.kind === 'check' ? 'N° de chèque obligatoire' : 'Référence obligatoire',
      });
    const remaining = remainingToCollect(total, this.paymentsOf(dossierId));
    if (input.amountCents > remaining)
      throw new LocalValidationError(`Montant supérieur au reste à encaisser (${formatEuros(remaining)})`, {
        amountCents: `Maximum ${formatEuros(remaining)}`,
      });
    const id = uuidv7();
    const payload = {
      dossierId,
      methodId: method.id,
      amountCents: input.amountCents,
      isDeposit: input.isDeposit ?? false,
      receivedOn: input.receivedOn ?? null,
      reference: input.reference?.trim() || null,
      comment: input.comment?.trim() || null,
    };
    const parsed = paymentCreateInput.safeParse({ ...payload, id });
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    await this.enqueue({ type: 'payment.create', entityId: id, payload });
    return id;
  }

  /** Confirmation de remise, annulation motivée, remboursement hors Stripe. */
  async transitionPayment(
    id: string,
    to: PaymentStatus,
    opts: { reasonId?: string | null; comment?: string | null; refundedCents?: number | null } = {},
  ): Promise<void> {
    const p = this.get('payment', id);
    if (!p) throw new LocalValidationError('Paiement introuvable');
    const def = paymentMachine.transitions.find(
      (t) => t.from.includes(p.status) && t.to === to && (t.actors ?? ['user', 'system']).includes('user'),
    );
    if (!def) throw new LocalValidationError('Changement de statut impossible');
    if (p.methodKind === 'stripe' && !['canceled', 'pending'].includes(to))
      throw new LocalValidationError('Paiement par carte : statut confirmé uniquement par Stripe');
    if (to === 'succeeded' && p.collectedById === this.opts.actor.userId)
      throw new LocalValidationError(
        'La remise doit être confirmée par une autre personne que celle qui a encaissé',
      );
    if (def.reasonCategory) {
      const reason = opts.reasonId ? this.get('reason', opts.reasonId) : undefined;
      if (!reason || reason.category !== def.reasonCategory || !reason.isActive)
        throw new LocalValidationError('Un motif est requis', { reasonId: 'Choisissez un motif' });
      if (reason.requiresComment && !opts.comment?.trim())
        throw new LocalValidationError('Un commentaire est requis', { comment: 'Précisez le motif' });
    }
    if (def.requiresComment && !opts.comment?.trim())
      throw new LocalValidationError('Un commentaire est requis', { comment: 'Commentaire obligatoire' });
    await this.enqueue({
      type: 'payment.transition',
      entityId: id,
      payload: {
        from: p.status,
        to,
        reasonId: opts.reasonId ?? null,
        comment: opts.comment?.trim() || null,
        refundedCents: opts.refundedCents ?? null,
      },
    });
  }

  /**
   * Lien portail client (§5) : le secret est créé ICI (même hors ligne) ; seul son SHA-256
   * part au serveur. Le lien fonctionne dès que la création est synchronisée.
   */
  async createPortalLink(
    dossierId: string,
    opts: {
      tenantId: string;
      days?: number;
      allowEdit?: boolean;
      allowUpload?: boolean;
      allowSign?: boolean;
      channel?: PortalChannel | null;
    },
  ): Promise<{ id: string; token: string }> {
    if (!this.get('dossier', dossierId)) throw new LocalValidationError('Dossier introuvable');
    const random =
      this.opts.randomBytes ?? ((n: number) => globalThis.crypto.getRandomValues(new Uint8Array(n)));
    const secret = portalSecret(random(32));
    const id = uuidv7();
    const token = portalToken(opts.tenantId, secret);
    const payload = {
      dossierId,
      tokenHash: await (this.opts.hash ?? sha256Hex)(utf8(secret)),
      expiresAt: new Date(Date.now() + (opts.days ?? PORTAL_DEFAULT_DAYS) * 86_400_000).toISOString(),
      allowEdit: opts.allowEdit ?? true,
      allowUpload: opts.allowUpload ?? true,
      allowSign: opts.allowSign ?? true,
      channel: opts.channel ?? null,
    };
    this.meta = { ...this.meta, portalTokens: { ...this.meta.portalTokens, [id]: token } };
    await this.enqueue({ type: 'portal.create', entityId: id, payload });
    return { id, token };
  }

  /** secret of a link created on this device (to share it again) */
  portalLinkToken(linkId: string): string | null {
    return this.meta.portalTokens?.[linkId] ?? null;
  }

  async revokePortalLink(linkId: string): Promise<void> {
    if (!this.get('portal_link', linkId)) return;
    await this.enqueue({ type: 'portal.revoke', entityId: linkId });
  }

  get unreadNotifications(): number {
    return this.list('notification').filter((n) => !n.readAt).length;
  }

  async markNotificationRead(id: string): Promise<void> {
    const n = this.get('notification', id);
    if (!n || n.readAt) return;
    await this.enqueue({ type: 'notification.read', entityId: id });
  }

  /** §15 : announcements for this user, live (pinned first) and unread count (badge). */
  liveAnnouncements(today = new Date().toISOString().slice(0, 10)) {
    return liveAnnouncements(this.list('announcement'), today);
  }

  get unreadAnnouncements(): number {
    return this.liveAnnouncements().filter((a) => !a.readAt && a.publishedById !== this.opts.actor.userId)
      .length;
  }

  async markAnnouncementRead(id: string): Promise<void> {
    const a = this.get('announcement', id);
    if (!a || a.readAt) return;
    await this.enqueue({ type: 'announcement.read', entityId: id });
  }

  /* ------------------------------------------------------------ prospects */

  /** My prospects to work (callbacks due first, then new ones), or all visible ones for a supervisor. */
  myProspects(today = new Date().toISOString().slice(0, 10)): ProspectRecord[] {
    return prospectsToWork(
      this.list('prospect').filter(
        (p) => p.assignedToId === this.opts.actor.userId && p.status !== 'archived',
      ),
      today,
    );
  }

  get prospectsToContact(): number {
    const today = new Date().toISOString().slice(0, 10);
    return this.myProspects(today).filter(
      (p) =>
        p.status === 'to_contact' || (p.status === 'callback' && (!p.callbackOn || p.callbackOn <= today)),
    ).length;
  }

  /** Enregistre un appel / une visite — possible hors ligne. */
  async contactProspect(
    id: string,
    input: { outcome: ProspectOutcome; comment?: string | null; callbackOn?: string | null },
  ): Promise<void> {
    const p = this.get('prospect', id);
    if (!p) throw new LocalValidationError('Prospect introuvable');
    if (p.status === 'archived') throw new LocalValidationError('Prospect archivé');
    const payload = {
      outcome: input.outcome,
      comment: input.comment?.trim() || null,
      callbackOn: input.callbackOn || null,
      at: new Date().toISOString(),
    };
    const parsed = prospectContactInput.safeParse(payload);
    if (!parsed.success) throw fieldErrors(parsed.error.issues);
    await this.enqueue({ type: 'prospect.contact', entityId: id, payload });
  }

  async linkProspectClient(id: string, clientId: string): Promise<void> {
    if (!this.get('prospect', id)) throw new LocalValidationError('Prospect introuvable');
    await this.enqueue({ type: 'prospect.link_client', entityId: id, payload: { clientId } });
  }

  async deleteComment(id: string): Promise<void> {
    if (!this.get('comment', id)) return;
    await this.enqueue({ type: 'comment.delete', entityId: id });
  }

  async dismissIssue(issueId: string) {
    this.meta = { ...this.meta, issues: this.meta.issues.filter((i) => i.id !== issueId) };
    await this.opts.storage.setMeta(this.meta);
    this.invalidate();
  }

  /* ------------------------------------------------------------ sync loop */

  start(intervalMs = 60_000) {
    this.stop();
    this.interval = setInterval(() => void this.sync(), intervalMs);
    void this.sync();
  }

  stop() {
    if (this.interval) clearInterval(this.interval);
    if (this.timer) clearTimeout(this.timer);
    this.interval = null;
    this.timer = null;
  }

  private scheduleSync() {
    const delay = this.opts.autoSyncDelayMs ?? 300;
    if (!delay) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.sync();
    }, delay);
  }

  /** Push then pull. Concurrent calls share the same run. Never throws. */
  sync(): Promise<void> {
    this.running ??= (async () => {
      const epoch = this.epoch;
      this.setStatus('syncing');
      try {
        await this.pushAll(epoch);
        await this.pullAll(epoch);
        this.inflight = [];
        // file contents after their metadata; a finished upload changes the file status
        if (await this.uploadAll(epoch)) await this.pullAll(epoch);
        this.meta = { ...this.meta, lastSyncAt: new Date().toISOString() };
        await this.opts.storage.setMeta(this.meta);
        this.setStatus('idle');
      } catch (err) {
        if (err instanceof ApiError && err.isNetwork) this.setStatus('offline', err.message);
        else if (err instanceof ApiError && (err.status === 401 || err.code === 'ACCOUNT_DISABLED'))
          this.setStatus('auth_required', err.message);
        else this.setStatus('error', err instanceof Error ? err.message : String(err));
      } finally {
        this.running = null;
        this.invalidate();
      }
    })();
    return this.running;
  }

  private assertEpoch(epoch: number) {
    if (epoch !== this.epoch) throw new Error('Synchronisation interrompue (session réinitialisée)');
  }

  private async pushAll(epoch: number) {
    const batchSize = this.opts.pushBatch ?? 50;
    while (this.outbox.length) {
      const batch = this.outbox.slice(0, batchSize);
      for (const e of batch) e.state = 'syncing';
      this.invalidate();
      let res: PushResponse;
      try {
        res = await this.opts.transport.push({
          deviceId: this.opts.deviceId,
          mutations: batch.map((e) => e.mutation),
        });
      } finally {
        for (const e of batch) e.state = 'pending';
      }
      this.assertEpoch(epoch);
      const byId = new Map<string, MutationResult>(res.results.map((r) => [r.mutationId, r]));
      const removed: number[] = [];
      const updated: OutboxEntry[] = [];
      let stop = false;
      const issues = [...this.meta.issues];
      for (const entry of batch) {
        const r = byId.get(entry.mutation.mutationId);
        if (!r) {
          stop = true;
          break;
        }
        if (r.retryable) {
          updated.push({ ...entry, attempts: entry.attempts + 1, lastError: r.error?.message });
          stop = true;
          break;
        }
        removed.push(entry.seq);
        if (r.status === 'applied') this.inflight.push(entry);
        if (r.status === 'partially_applied') {
          this.inflight.push(entry);
          issues.push(
            issueFrom(
              entry,
              'conflict',
              'Modifié entre-temps par un autre utilisateur : la version du serveur a été conservée pour certains champs',
              r.conflicts,
            ),
          );
        }
        if (r.status === 'rejected') {
          issues.push(
            issueFrom(entry, 'failed', r.error?.message ?? 'Refusé par le serveur', r.error?.details),
          );
        }
      }
      this.meta = { ...this.meta, issues: issues.slice(-100) };
      await this.opts.storage.ackOutbox(removed, updated, this.meta);
      this.outbox = this.outbox
        .filter((e) => !removed.includes(e.seq))
        .map((e) => updated.find((u) => u.seq === e.seq) ?? e);
      this.invalidate();
      if (stop)
        throw new Error(updated[0]?.lastError ?? 'Synchronisation interrompue, nouvel essai plus tard');
    }
  }

  private async pullAll(epoch: number) {
    for (let i = 0; i < 10_000; i++) {
      const res = await this.opts.transport.pull(this.meta.cursor, this.opts.pullLimit);
      this.assertEpoch(epoch);
      if (res.reset) {
        this.meta = { ...this.meta, cursor: '' };
        await this.opts.storage.applyPull([], this.meta, true);
        this.records = emptyRecords();
        this.invalidate();
        continue;
      }
      this.meta = { ...this.meta, cursor: res.cursor };
      await this.opts.storage.applyPull(res.changes, this.meta, false);
      applyChanges(this.records, res.changes);
      if (res.changes.length) this.invalidate();
      if (!res.hasMore) return;
    }
  }

  /**
   * Sends local file contents, chunk by chunk, resuming where the server stopped.
   * Returns true when at least one file became complete.
   */
  private async uploadAll(epoch: number): Promise<boolean> {
    const { blobs, transport } = { blobs: this.opts.blobs, transport: this.opts.transport };
    if (!blobs || !transport.uploadChunk || !transport.uploadStatus) return false;
    let completed = false;
    for (const id of await blobs.list()) {
      this.assertEpoch(epoch);
      if (this.isPending('dossier_file', id)) continue; // metadata not confirmed yet
      const rec = this.records.dossier_file[id];
      if (!rec || rec.status === 'ready') {
        // refused / deleted on the server, or already received: nothing more to send
        await blobs.delete(id);
        this.localFiles.delete(id);
        this.invalidate();
        continue;
      }
      if (await this.uploadOne(id, blobs, transport as Required<SyncTransport>, epoch)) completed = true;
    }
    return completed;
  }

  private async uploadOne(
    id: string,
    blobs: BlobStore,
    transport: Required<SyncTransport>,
    epoch: number,
  ): Promise<boolean> {
    let state: UploadState;
    try {
      state = await transport.uploadStatus(id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        await blobs.delete(id);
        this.localFiles.delete(id);
        return false;
      }
      throw err;
    }
    let restarts = 0;
    while (state.status !== 'ready') {
      this.assertEpoch(epoch);
      this.localFiles.set(id, { sent: state.receivedBytes, total: state.sizeBytes });
      this.invalidate();
      const end = Math.min(state.receivedBytes + state.chunkBytes, state.sizeBytes);
      const bytes = (await blobs.read(id, state.receivedBytes, end)) ?? new Uint8Array();
      try {
        state = await transport.uploadChunk(id, state.receivedBytes, bytes);
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.code === 'CONFLICT' &&
          typeof (err.details as { receivedBytes?: unknown })?.receivedBytes === 'number'
        ) {
          state = { ...state, receivedBytes: (err.details as { receivedBytes: number }).receivedBytes };
          continue;
        }
        if (err instanceof ApiError && err.status === 422 && restarts++ < 1) {
          state = { ...state, receivedBytes: 0 };
          continue;
        }
        if (err instanceof ApiError && !err.isNetwork && err.status < 500) {
          // content refused (type does not match…): it will not get better — report it and drop it
          this.meta = {
            ...this.meta,
            issues: [
              ...this.meta.issues,
              {
                id: uuidv7(),
                mutationId: id,
                type: 'file.create' as const,
                entityId: id,
                kind: 'failed' as const,
                message: `Envoi du fichier refusé : ${err.message}`,
                at: new Date().toISOString(),
              },
            ].slice(-100),
          };
          await this.opts.storage.setMeta(this.meta);
          await blobs.delete(id);
          this.localFiles.delete(id);
          this.invalidate();
          return false;
        }
        throw err;
      }
    }
    await blobs.delete(id);
    this.localFiles.delete(id);
    this.invalidate();
    return true;
  }

  /** Wipes local data (logout / user switch). Pending changes are lost: callers must warn. */
  async reset() {
    this.stop();
    this.epoch++;
    await this.running?.catch(() => undefined);
    await this.opts.storage.clear();
    for (const id of (await this.opts.blobs?.list()) ?? []) await this.opts.blobs?.delete(id);
    this.localFiles.clear();
    this.records = emptyRecords();
    this.outbox = [];
    this.inflight = [];
    this.meta = emptyMeta();
    this.invalidate();
  }
}

function issueFrom(
  entry: OutboxEntry,
  kind: SyncIssue['kind'],
  message: string,
  details?: unknown,
): SyncIssue {
  return {
    id: uuidv7(),
    mutationId: entry.mutation.mutationId,
    type: entry.mutation.type,
    entityId: entry.mutation.entityId,
    kind,
    message,
    details,
    at: new Date().toISOString(),
  };
}

function diffChanges(current: object, patch: object, allowed: readonly string[]) {
  const changes: Record<string, { from: unknown; to: unknown }> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (!allowed.includes(k) || v === undefined) continue;
    const from = (current as Record<string, unknown>)[k] ?? null;
    const to = v === '' ? null : v;
    if (!valuesEqual(from, to)) changes[k] = { from, to };
  }
  return changes;
}

function fieldErrors(issues: { path: PropertyKey[]; message: string }[]) {
  return new LocalValidationError(
    'Données invalides',
    Object.fromEntries(issues.map((i) => [i.path.map(String).join('.') || 'form', i.message])),
  );
}

export type { ClientRecord };
